/* ==========================================================================
   8TrackFit - offer engine: free-delivery bars + spin-reward strip +
   discount picker.

   What runs now:
     1. FREE DELIVERY at $100 - REAL discount code FREESHIP100 (free shipping,
        min subtotal $100). The qualifying total counts INDIVIDUAL PRODUCTS
        ONLY: every line whose product_type is "Bundle" is excluded, EXCEPT
        the one bundle the client cleared for it - Basic Package (see
        SHIP_BUNDLES). The [data-tf-offer3] bars track that same eligible
        total, so what a shopper sees is what the cart will get.
     2. The offer strip ([data-tf-ostrip]) - ONLY while a Spin & Win
        reservation is active. No spin, no strip.
     3. WHICH real Shopify discount rides the cart:
          BUY3SAVE10 (10%)  once the cart has 3+ items
          FREESHIP100       once the eligible total reaches $100
          SPIN* codes       while a spin reservation (15 min) is active
          nothing           when no offer qualifies

   ------------------------------------------------------------------
   COUNTDOWN (rebuilt 2026-09-17 - it stuttered and skipped seconds)
   The old clock was repainted by the 1s state tick, which rewrote the whole
   strip sentence with innerHTML on every beat. Two faults: setInterval drifts
   (a 1000ms beat lands at 1001, 1003... so displayed seconds occasionally
   jump two), and replacing innerHTML re-created the text every second inside
   an element that also runs a background transition and a keyframe pulse.
   Now:
     - the sentence is written ONCE per prize (it no longer contains the time)
     - the clock lives only in [data-tf-ostrip-time], shows hundredths
       (m:ss.cs) and is driven by requestAnimationFrame
     - writeClock() only touches textContent on a leaf node, and only when
       the string actually changed
   BELT AND BRACES: rAF does not run in a hidden/background tab, and an
   rAF-only clock renders BLANK there - so the 1s tick calls writeClock too.

   PROGRESS FILL (2026-09-17): the bar animates transform: scaleX(), not
   width. Width is a layout property, so the old half-second grow ran layout
   + paint every frame inside the cart drawer and stuttered on cheap phones;
   a transform is composited. The markup may still carry an inline
   style="width: 0%" from older templates - paintOfferBars clears it, or the
   bar would be squashed to nothing regardless of the scale.
   ------------------------------------------------------------------

   CUSTOMER-TYPED CODES ARE NEVER TOUCHED (fixed 2026-09-17)
   /cart/update.js REPLACES the whole code set, so every write used to wipe
   whatever the shopper had entered themselves: someone applied an influencer
   code, added enough to pass $100, and the engine swapped their code for
   FREESHIP100 without a word (reproduced live: FLASH15 replaced at $102).
   Every write now re-reads the cart first, keeps every code that is not one
   of OURS (isOurs: the configured offer codes plus anything starting SPIN),
   and appends our own on top. Foreign codes are re-read on each write, never
   cached - so when the shopper deletes their code, it stays deleted.

   REJECTED-CODE GUARD (added 2026-09-17 after a live bug):
   BUY3SAVE10 EXPIRED on 2026-09-09 while the theme kept applying it to every
   3+ item cart. Shopify accepted the code onto the cart but marked it
   applicable:false, so shoppers got NO discount - and worse, the picker
   preferred it over a just-won SPIN10 (equal percentage, base wins), which
   silently swallowed spin prizes. Any code of OURS that Shopify flags
   applicable:false is now recorded against the cart signature, dropped, and
   skipped by the picker, which re-runs without it. A foreign code that does
   not apply is left alone - that is the shopper's business.

   NOTE the force flag on syncDiscount: clearing our record of what is applied
   is NOT enough to remove a dead code, because the "nothing to do" guard
   (target === have) then sees '' === '' and skips the write. Removal has to
   be an unconditional POST.

   ORDERING HAZARD: cartKnown blocks discount decisions until the first
   /cart.js response. Every DOM write is guarded by a comparison - the cart
   drawer re-renders on each cart change and an unconditional write would
   loop its observer.
   ========================================================================== */

(function () {
  'use strict';

  var root = document.getElementById('tf-offer');
  if (!root) return;

  var CFG = {
    url: root.dataset.url || '/collections/all',
    multiCode: 'BUY3SAVE10',
    multiPercent: 10,
    multiMin: 3,
    shipCode: 'FREESHIP100',
    shipThreshold: 10000 /* cents - free delivery at $100 or more */
  };

  /* Bundles are excluded from free delivery EXCEPT these handles - the
     client picked Basic Package. Add a handle here to clear another one. */
  var SHIP_BUNDLES = ['basic-package'];

  var K_APPLIED = 'tf_offer_applied';
  var K_SPIN = 'tf_spin';

  /* a code this engine owns - everything else on the cart belongs to the
     shopper and must survive our writes */
  function isOurs(code) {
    var c = String(code || '').toUpperCase();
    if (!c) return false;
    if (c === CFG.multiCode || c === CFG.shipCode) return true;
    return c.indexOf('SPIN') === 0; /* every Spin & Win prize code */
  }

  function store(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      if (value === null) window.localStorage.removeItem(key);
      else window.localStorage.setItem(key, value);
    } catch (e) {}
    return null;
  }

  function setText(el, value) { if (el && el.textContent !== value) el.textContent = value; }
  function setHTML(el, value) { if (el && el.innerHTML !== value) el.innerHTML = value; }
  function setAttr(el, name, value) { if (el && el.getAttribute(name) !== value) el.setAttribute(name, value); }

  function pad2(n) { return n < 10 ? '0' + n : '' + n; }

  /* m:ss.cs - hundredths, so the number is always visibly moving */
  function clockMs(ms) {
    var t = ms > 0 ? ms : 0;
    var whole = Math.floor(t / 1000);
    var m = Math.floor(whole / 60);
    var s = whole % 60;
    var cs = Math.floor((t % 1000) / 10);
    return m + ':' + pad2(s) + '.' + pad2(cs);
  }

  /* ---------- spin & win reservation ---------- */

  function getSpin() {
    var raw = store(K_SPIN);
    if (!raw) return null;
    try {
      var s = JSON.parse(raw);
      if (!s || !s.code || !s.until) return null;
      if (Date.now() >= s.until) { store(K_SPIN, null); return null; }
      return s;
    } catch (e) { store(K_SPIN, null); return null; }
  }

  /* ---------- cart state + best-offer picker ---------- */

  var cartCount = 0;
  var cartTotal = 0;      /* every line, after discounts */
  var cartEligible = 0;   /* what counts toward free delivery */
  var cartHasBundle = false;
  var cartKnown = false;
  var syncing = false;
  var pendingSync = false;

  /* codes of OURS Shopify refused, against the cart state they failed on */
  var rejected = {};

  function cartSig() { return cartCount + ':' + cartTotal; }
  function usable(code) { return rejected[code] !== cartSig(); }

  function isBundleLine(item) {
    var t = (item && item.product_type ? String(item.product_type) : '').toLowerCase();
    return t === 'bundle';
  }

  function countsForShipping(item) {
    if (!isBundleLine(item)) return true;
    var handle = (item && item.handle ? String(item.handle) : '').toLowerCase();
    return SHIP_BUNDLES.indexOf(handle) !== -1;
  }

  function baseOffer() {
    if (cartCount >= CFG.multiMin && usable(CFG.multiCode)) {
      return { code: CFG.multiCode, pct: CFG.multiPercent };
    }
    return null;
  }

  function shipUnlocked() {
    return cartEligible >= CFG.shipThreshold && usable(CFG.shipCode);
  }

  // The comma-joined set of OUR codes the cart should carry right now.
  function desiredTarget() {
    var spin = getSpin();
    if (spin && !usable(spin.code)) spin = null;
    var base = baseOffer();
    var codes = [];

    if (spin && spin.kind === 'bogo') {
      return spin.code; /* BOGO cannot combine with anything */
    }
    if (spin && spin.kind !== 'ship') {
      if (base && base.pct >= (spin.pct || 0)) codes.push(base.code);
      else codes.push(spin.code);
    } else if (base) {
      codes.push(base.code);
    }

    if (shipUnlocked()) {
      codes.push(CFG.shipCode);
    } else if (spin && spin.kind === 'ship' && cartEligible > 0) {
      codes.push(spin.code);
    }

    return codes.join(',');
  }

  /* codes on the cart that the shopper put there */
  function foreignCodes(cart) {
    var out = [];
    var codes = (cart && cart.discount_codes) || [];
    for (var i = 0; i < codes.length; i++) {
      var c = codes[i] && codes[i].code;
      if (c && !isOurs(c) && out.indexOf(c) === -1) out.push(c);
    }
    return out;
  }

  function verifyApplied() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var codes = cart.discount_codes || [];
        var bad = false;
        for (var i = 0; i < codes.length; i++) {
          if (codes[i] && codes[i].applicable === false && isOurs(codes[i].code)) {
            rejected[codes[i].code] = cartSig();
            bad = true;
          }
        }
        if (bad) {
          store(K_APPLIED, null);
          syncDiscount(true); /* force: the removal write must not be skipped */
        }
      })
      .catch(function () {});
  }

  function syncDiscount(force) {
    if (!cartKnown) return;

    var target = desiredTarget();
    var have = store(K_APPLIED) || '';

    if (!force && target === have) return;
    if (syncing) { pendingSync = true; return; }
    syncing = true;

    var settle = function () {
      syncing = false;
      if (pendingSync) { pendingSync = false; syncDiscount(); }
    };

    /* re-read the cart immediately before writing: whatever the shopper has
       entered since our last look has to be carried through the write */
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var keep = foreignCodes(cart);
        var ours = target ? target.split(',') : [];
        for (var i = 0; i < ours.length; i++) {
          if (ours[i] && keep.indexOf(ours[i]) === -1) keep.push(ours[i]);
        }

        store(K_APPLIED, target || null);

        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ discount: keep.join(',') })
        });
      })
      .then(function () {
        settle();
        if (target) verifyApplied();
      })
      .catch(function () {
        store(K_APPLIED, have || null);
        settle();
      });
  }

  function refreshCart() {
    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        cartCount = cart.item_count || 0;
        cartTotal = cart.total_price || 0;

        var items = cart.items || [];
        var eligible = 0;
        var bundled = false;
        for (var i = 0; i < items.length; i++) {
          var line = items[i];
          var value = line.final_line_price;
          if (typeof value !== 'number') value = line.line_price || 0;
          if (isBundleLine(line)) bundled = true;
          if (countsForShipping(line)) eligible += value;
        }
        cartEligible = eligible;
        cartHasBundle = bundled;

        cartKnown = true;

        var codes = cart.discount_codes || [];
        var stale = false;
        for (var j = 0; j < codes.length; j++) {
          if (codes[j] && codes[j].applicable === false && isOurs(codes[j].code)) {
            rejected[codes[j].code] = cartSig();
            stale = true;
          }
        }

        syncDiscount(stale);
        paintOfferBars();
      })
      .catch(function () {});
  }

  function offerHref() {
    var t = desiredTarget();
    if (!t) return CFG.url;
    return '/discount/' + encodeURIComponent(t.split(',')[0]) + '?redirect=' + encodeURIComponent(CFG.url);
  }

  /* ---------- free-delivery progress bars ---------- */

  function fmtMoney(cents) {
    var v = cents / 100;
    return '$' + (v % 1 === 0 ? v.toFixed(0) : v.toFixed(2));
  }

  function paintOfferBars() {
    var bars = document.querySelectorAll('[data-tf-offer3]');
    for (var i = 0; i < bars.length; i++) {
      var bar = bars[i];
      var thresh = parseInt(bar.dataset.threshold, 10) || CFG.shipThreshold;
      var basis = cartEligible; /* bundles do not count toward free delivery */
      var have = Math.min(basis, thresh);
      var state = basis >= thresh ? 'unlocked' : 'locked';
      if (bar.dataset.state !== state) bar.dataset.state = state;

      var text;
      if (basis >= thresh) {
        text = '<strong>FREE DELIVERY</strong> unlocked - applied at checkout.';
      } else if (cartHasBundle && basis === 0) {
        text = 'Add ' + fmtMoney(thresh) + ' of individual products to get <strong>FREE DELIVERY</strong>';
      } else {
        text = 'Add ' + fmtMoney(thresh - basis) + ' more to get <strong>FREE DELIVERY</strong>';
      }

      setHTML(bar.querySelector('[data-tf-offer3-text]'), text);
      setText(bar.querySelector('[data-tf-offer3-count]'), fmtMoney(have) + ' / ' + fmtMoney(thresh));

      var fill = bar.querySelector('[data-tf-offer3-fill]');
      if (fill) {
        /* composited scale, not a width animation - see the header note */
        var ratio = thresh > 0 ? Math.min(1, Math.max(0, have / thresh)) : 0;
        var t = 'scaleX(' + (Math.round(ratio * 1000) / 1000) + ')';
        if (fill.style.transform !== t) fill.style.transform = t;
        /* older markup ships style="width: 0%" inline, which would keep the
           bar at zero no matter what we scale */
        if (fill.style.width) fill.style.width = '';
      }
    }
  }

  /* ---------- the spin-reward strip ---------- */

  var TEMPLATE = document.getElementById('tf-offer-strip-template');
  var BAR_TEMPLATE = document.getElementById('tf-offer-bar-template');

  var spinCache = getSpin();
  var deadline = spinCache ? spinCache.until : 0;
  var timeNodes = [];
  var lastClock = '';

  function clearCloseButton(el) {
    if (el.dataset.tfCleared === '1') return;
    if (!el.classList.contains('tf-ostrip--drawer')) { el.dataset.tfCleared = '1'; return; }
    var btn = document.querySelector('.cart-drawer__close-button');
    if (!btn) { el.dataset.tfCleared = '1'; return; }
    var br = btn.getBoundingClientRect();
    var er = el.getBoundingClientRect();
    if (!br.width || !er.width) return; /* drawer hidden - re-check next tick */
    var collide = er.top < br.bottom + 6 && er.bottom > br.top && er.right > br.left;
    if (collide) el.style.marginTop = Math.ceil(br.bottom + 8 - er.top) + 'px';
    el.dataset.tfCleared = '1';
  }

  /* Strip policy: visible ONLY while a spin reservation runs. The hidden
     attribute alone was not enough - .tf-ostrip sets display:flex, which
     outranks the UA [hidden] rule, so an EMPTY black strip with a Shop-now
     button sat in every cart drawer. */
  function paintStrip(el) {
    var spin = spinCache;
    var show = !!spin;
    if (el.hidden === show) el.hidden = !show;
    var wanted = show ? '' : 'none';
    if (el.style.display !== wanted) el.style.display = wanted;
    if (!show) return;

    clearCloseButton(el);

    if (el.dataset.state !== 'urgent') el.dataset.state = 'urgent';
    if (el.style.getPropertyValue('--tf-ostrip-heat') !== '1') {
      el.style.setProperty('--tf-ostrip-heat', '1');
    }

    /* the sentence carries NO clock - written once per prize, while the
       countdown lives in its own node and is animated by rAF */
    setHTML(el.querySelector('[data-tf-ostrip-text]'),
      '🎁 Your spin reward - <strong>' + spin.prize + '</strong> - is saved in your cart. Check out before the timer runs out.');

    var cta = el.querySelector('[data-tf-ostrip-cta]');
    setText(cta, 'Shop now');
    setAttr(cta, 'href', offerHref());
  }

  function strips() {
    return Array.prototype.slice.call(document.querySelectorAll('[data-tf-ostrip]'));
  }

  /* ---------- the countdown ---------- */

  function collectTimeNodes() {
    timeNodes = Array.prototype.slice.call(
      document.querySelectorAll('[data-tf-ostrip] [data-tf-ostrip-time]')
    );
    lastClock = ''; /* force the next write into freshly mounted nodes */
  }

  function writeClock() {
    if (!deadline || !timeNodes.length) return;
    var txt = clockMs(deadline - Date.now());
    if (txt === lastClock) return;
    lastClock = txt;
    for (var i = 0; i < timeNodes.length; i++) {
      /* textContent on a leaf node - no innerHTML, no layout thrash */
      if (timeNodes[i].textContent !== txt) timeNodes[i].textContent = txt;
    }
  }

  function frame() {
    writeClock();
    window.requestAnimationFrame(frame);
  }
  window.requestAnimationFrame(frame);

  function mountStrip() {
    if (!TEMPLATE || !TEMPLATE.content.firstElementChild) return;

    var inner = document.querySelector('.cart-drawer__inner');
    if (inner && !inner.querySelector('[data-tf-ostrip]')) {
      var node = TEMPLATE.content.firstElementChild.cloneNode(true);
      node.classList.add('tf-ostrip--drawer');
      var header = inner.querySelector('.cart-drawer__header');
      if (header && header.parentNode) header.parentNode.insertBefore(node, header.nextSibling);
      else inner.insertBefore(node, inner.firstChild);
      wireStrip(node);
    }

    if (inner && BAR_TEMPLATE && BAR_TEMPLATE.content.firstElementChild && !inner.querySelector('[data-tf-offer3]')) {
      var stripNode = inner.querySelector('[data-tf-ostrip]');
      if (stripNode && stripNode.parentNode) {
        var barNode = BAR_TEMPLATE.content.firstElementChild.cloneNode(true);
        stripNode.parentNode.insertBefore(barNode, stripNode.nextSibling);
        paintOfferBars();
      }
    }

    /* every declared slot gets its own strip - cart page AND the footer band */
    var slots = document.querySelectorAll('[data-tf-ostrip-slot]');
    for (var i = 0; i < slots.length; i++) {
      if (!slots[i].querySelector('[data-tf-ostrip]')) {
        var pageNode = TEMPLATE.content.firstElementChild.cloneNode(true);
        slots[i].appendChild(pageNode);
        wireStrip(pageNode);
      }
    }

    collectTimeNodes();
    writeClock();
  }

  function wireStrip(node) {
    var cta = node.querySelector('[data-tf-ostrip-cta]');
    if (cta) cta.addEventListener('click', function () { syncDiscount(); });
    paintStrip(node);
  }

  /* ---------- 1s state tick (owns state; also a clock safety net) ---------- */

  function tick() {
    var spinNow = getSpin();
    var had = !!spinCache;
    spinCache = spinNow;
    deadline = spinNow ? spinNow.until : 0;

    strips().forEach(paintStrip);

    /* reservation appearing or lapsing -> re-decide the cart's codes */
    if (had !== !!spinNow) {
      collectTimeNodes();
      syncDiscount();
    }

    /* keeps a number on screen even where rAF never runs */
    writeClock();
  }

  tick();
  window.setInterval(tick, 1000);

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) tick();
  });

  /* ---------- wiring ---------- */

  mountStrip();
  refreshCart();

  var drawer = document.querySelector('cart-drawer-component');
  if (drawer && 'MutationObserver' in window) {
    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      window.setTimeout(function () {
        queued = false;
        mountStrip();
        tick();
      }, 80);
    }).observe(drawer, { childList: true, subtree: true });
  }

  document.addEventListener('cart:update', function () {
    window.setTimeout(function () {
      mountStrip();
      refreshCart();
      tick();
    }, 60);
  });
})();
